import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';

/**
 * A component that detects and blocks developer tools.
 * Renders a premium, centered React modal for Right-click warnings.
 */
const DevToolsDetector = () => {
  const location = useLocation();
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    // Function to redirect the user to the "gotcha" page.
    const redirectToGotcha = () => {
      // Set a flag to prevent multiple, rapid redirects.
      if ((window as any).isRedirecting) return;
      (window as any).isRedirecting = true;
      window.location.replace('/gotcha.html');
    };

    // Don't run any detection if we are already on the target page.
    if (location.pathname.endsWith('/gotcha.html')) {
      return;
    }

    // --- RELIABLE DETECTION METHODS ---

    // 1. Block common keyboard shortcuts for developer tools and view source.
    const handleKeyDown = (e: KeyboardEvent) => {
      // Covers F12, View Source (Ctrl+U / Cmd+U), and Inspect Element variants
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
        (e.metaKey && e.altKey && (e.key === 'I' || e.key === 'J' || e.key === 'C' || e.key === 'U')) ||
        (e.ctrlKey && e.key === 'U') ||
        (e.metaKey && e.shiftKey && e.key === 'C')
      ) {
        e.preventDefault(); // Stop the browser from opening the tools.
        redirectToGotcha(); // Redirect the user.
      }
    };

    // 2. Block Right Click (Context Menu) and show custom React Modal
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      setShowWarning(true);
    };


    // --- Attach Event Listeners ---
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('contextmenu', handleContextMenu);

    // The cleanup function removes event listeners when the component is no longer in use.
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [location.pathname]);

  if (!showWarning) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="bg-red-50 p-6 flex flex-col items-center justify-center text-center border-b border-red-100">
          <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <ShieldAlert className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Security Notice</h2>
          <p className="text-slate-500 mt-2 text-sm">
            Developer tools and context menus are restricted on this platform to protect sensitive data.
          </p>
        </div>
        
        <div className="p-6">
          <p className="text-center font-medium text-slate-700 mb-6">
            Did you right-click by mistake?
          </p>
          <div className="flex flex-col gap-3">
            <button 
              onClick={() => setShowWarning(false)}
              className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors"
            >
              Yes, it was a mistake (Stay on page)
            </button>
            <button 
              onClick={() => window.location.replace('/gotcha.html')}
              className="w-full py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-medium transition-colors"
            >
              No, I want to Inspect Element
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevToolsDetector;